import { ChipRow } from "./CategoryChips";
import { Badge } from "@/components/ui/badge";

export interface QuickAmenities {
  wifi?: "yes" | "no";
  outlets?: "plenty" | "some" | "none";
  seating?: "large" | "small";
  parking?: "free_street" | "paid_street" | "none";
  food?: "full_food" | "pastries_and_drinks" | "drinks_only";
  noise?: "quiet" | "moderate" | "loud";
}

interface Props {
  amenities: QuickAmenities;
  onChange: (next: QuickAmenities) => void;
}

// Each row: display labels + value mapping. "Not sure" = clearing the selection.
const ROWS: Array<{
  key: keyof QuickAmenities;
  label: string;
  options: Array<{ label: string; value: string }>;
}> = [
  {
    key: "wifi",
    label: "WiFi",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
  },
  {
    key: "outlets",
    label: "Outlets",
    options: [
      { label: "Plenty", value: "plenty" },
      { label: "Some", value: "some" },
      { label: "None", value: "none" },
    ],
  },
  {
    key: "seating",
    label: "Seating",
    options: [
      { label: "Lots", value: "large" },
      { label: "Limited", value: "small" },
    ],
  },
  {
    key: "parking",
    label: "Parking",
    options: [
      { label: "Free", value: "free_street" },
      { label: "Paid", value: "paid_street" },
      { label: "None", value: "none" },
    ],
  },
  {
    key: "food",
    label: "Food",
    options: [
      { label: "Full menu", value: "full_food" },
      { label: "Pastries only", value: "pastries_and_drinks" },
      { label: "Drinks only", value: "drinks_only" },
    ],
  },
  {
    key: "noise",
    label: "Noise level",
    options: [
      { label: "Quiet", value: "quiet" },
      { label: "Moderate", value: "moderate" },
      { label: "Loud", value: "loud" },
    ],
  },
];

export function QuickAmenitiesSection({ amenities, onChange }: Props) {
  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground">What do you know about this café?</p>
          <Badge variant="secondary" className="text-[10px] px-2 py-0 h-5 bg-[#f5ecd7] text-foreground/60 border-0">
            Optional
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Optional — share what you know and we'll take care of the rest.
        </p>
      </div>

      <div className="space-y-3 rounded-lg bg-[#f5ecd7]/40 border border-[#f5ecd7]/60 p-3">
        {ROWS.map((row) => {
          const currentValue = amenities[row.key];
          // Map stored value back to chip label for ChipRow's selected state
          const selectedLabel =
            row.options.find((o) => o.value === currentValue)?.label || "";

          return (
            <ChipRow
              key={row.key}
              label={row.label}
              options={row.options.map((o) => o.label)}
              selected={selectedLabel}
              onSelect={(v) => {
                const labelStr = Array.isArray(v) ? v[0] : v;
                if (!labelStr) {
                  // Cleared = "Not sure"
                  const next = { ...amenities };
                  delete next[row.key];
                  onChange(next);
                  return;
                }
                const matched = row.options.find((o) => o.label === labelStr);
                if (matched) {
                  onChange({ ...amenities, [row.key]: matched.value as never });
                }
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
